// Customizable Area Start
import { BlockComponent } from "../../../framework/src/BlockComponent";
import { IBlock } from "../../../framework/src/IBlock";
import { runEngine } from "../../../framework/src/RunEngine";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { Message } from "../../../framework/src/Message";
import { ChoiceOptions, NewEveryreelSteps, PostTypes } from "./enums";
import { File, NormalizedFile, Choice, FunnelFileData } from "./types";
import React from "react";
import { getToken } from "../../../components/src/AuthService";
import * as htmlToImage from 'html-to-image';

export const configJSON = require("./config");

export type Props = {
  classes: any;
  files: File[];
  setStep: (step: NewEveryreelSteps) => void;
  closeModal: () => void;
  addFiles: (files: File[]) => void;
  postType: PostTypes;
  savedFunnelFiles: FunnelFileData[];
  removeChoice: (choiceIndex: number) => void;
  isUploading?: boolean;
};

interface S {
  files: NormalizedFile[];
  selectedFile: NormalizedFile;
  currentChoiceOption: ChoiceOptions;
  error: string;
  allowMultipleSelection: boolean;
  choicesArriveTime: number;
  isLoading: boolean;
  thumbnail: string;
  isEditMode: boolean;
}

interface SS {
  navigation: any;
  stepType: NewEveryreelSteps;
}

export default class CreateEveryreelConfirmSettings extends BlockComponent<
  Props,
  S,
  SS
> {
  addFileButtonRef: React.RefObject<HTMLInputElement>;
  videoPreviewRef: React.RefObject<HTMLVideoElement>;
  linkButtonFieldRef: React.RefObject<HTMLInputElement> = React.createRef()
  sendSocialScrollApiCallId = "";
  sendFunnelApiCallId = "";

  constructor(props: Props) {
    super(props);
    this.subScribedMessages = [getName(MessageEnum.RestAPIResponceMessage)];
    this.addFileButtonRef = React.createRef();
    this.videoPreviewRef = React.createRef();

    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    this.state = {
      files: this.normalizeFiles(this.props.files),
      selectedFile: this.normalizeFiles(this.props.files)[0],
      currentChoiceOption: 0,
      allowMultipleSelection: true,
      choicesArriveTime: 3,
      error: "",
      isLoading: false,
      thumbnail: "",
      isEditMode: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    // Handle new files being added
    if (prevProps?.files?.length !== this.props?.files?.length) {
      const newFiles = this.props.files.slice(prevProps.files.length);
      if (newFiles.length > 0) {
        const normalizedNewFiles = this.normalizeFiles(newFiles, false);
        this.setState((prevState) => ({
          files: [...prevState.files, ...normalizedNewFiles],
        }));
      }
    }
  }

  postPreview = configJSON.postPreview;
  postSocialScrollText = configJSON.postSocialScroll;
  postEveryreelText = configJSON.postEveryreelText;
  upload = configJSON.upload;
  linkButton = configJSON.linkButton;
  videos = configJSON.videos;
  editChoices = configJSON.editChoices;

  selectFile = (fileIndex: number) => {
    console.log('selectFile called with index:', fileIndex);
    this.setState((prevState) => {
      let selectedFile = prevState.selectedFile;

      const updatedFiles = prevState.files.map((file, i) => {
        if (file.selected && fileIndex !== i) {
          return {
            ...prevState.selectedFile,
            selected: false,
          };
        } else if (fileIndex === i) {
          selectedFile = file;
          console.log('Selected file:', selectedFile?.title, 'with choices:', selectedFile?.choices);
          return {
            ...file,
            selected: true,
          };
        } else {
          return file;
        }
      });

      return {
        files: updatedFiles,
        selectedFile,
      };
    });

    this.videoPreviewRef.current?.load();
  };

  setPostTitle = (value: string) => {
    this.setState((prevState) => {
      const files = prevState.files.map((file) =>
        file.selected ? { ...file, title: value } : file
      );
      return {
        selectedFile: { ...prevState.selectedFile, title: value },
        files
      };
    });
  };

  setPostCaption = (value: string) => {
    this.setState((prevState) => {
      const updatedFileDetails = { ...prevState.selectedFile, caption: value };

      const files = prevState.files.map((file) =>
        file.selected ? { ...file, caption: value } : file
      );

      return {
        selectedFile: updatedFileDetails,
        files,
      };
    });
  };

  setLinkText = (value: string) => {
    this.setState((prevState) => {
      return {
        selectedFile: { ...prevState.selectedFile, linkText: value },
      };
    });
  };

  setLinkURL = (value: string) => {
    this.setState((prevState) => {
      return {
        selectedFile: { ...prevState.selectedFile, link: value },
      };
    });
  };

  allowLink = () => {
    this.setState((prevState) => ({
      selectedFile: {
        ...prevState.selectedFile,
        isLinkAllowed: !prevState.selectedFile.isLinkAllowed,
      },
    }));
  };

  removeChoice = (choiceIndex: number) => {
    this.setState((prevState) => {
      const { selectedFile, files } = prevState;

      // Remove selected choice
      const removedChoice = selectedFile.choices[choiceIndex];
      const updatedChoices = selectedFile.choices.filter(
        (_, index) => index !== choiceIndex
      );

      // Track ids of already-saved choices the user removed, so postFunnel can
      // send them back as `{ id, _destroy: true }` — dropping them from the
      // payload alone does not delete them server-side.
      const updatedRemovedChoiceIds =
        removedChoice?.id != null
          ? [...(selectedFile.removedChoiceIds || []), removedChoice.id]
          : selectedFile.removedChoiceIds;

      // Updated selected file
      const updatedSelectedFile = {
        ...selectedFile,
        choices: updatedChoices,
        removedChoiceIds: updatedRemovedChoiceIds,
      };

      // Update inside files array also
      const updatedFiles = files.map((file) => {
        // Match current selected file
        if (
          file.id === selectedFile.id ||
          file.title === selectedFile.title
        ) {
          return {
            ...file,
            choices: updatedChoices,
            removedChoiceIds: updatedRemovedChoiceIds,
          };
        }

        return file;
      });

      return {
        selectedFile: updatedSelectedFile,
        files: updatedFiles,
      };
    });
  };

  handleLinkButtonTextEdit = () => {
    if(this.linkButtonFieldRef.current) {
      this.linkButtonFieldRef.current.focus()
    }
  }

  toggleEditMode = () => {
    this.setState((prevState) => ({
      isEditMode: !prevState.isEditMode,
    }));
  }

  removeFile = (fileIndex: number) => {
    this.setState((prevState) => {
      if (prevState.files.length <= 1) {
        return null;
      }

      const removedFile = prevState.files[fileIndex];
      const updatedFiles = prevState.files.filter((_, i) => i !== fileIndex);

      let selectedFile = prevState.selectedFile;
      if (removedFile?.selected && updatedFiles.length > 0) {
        const newSelectedIndex = Math.min(fileIndex, updatedFiles.length - 1);
        updatedFiles[newSelectedIndex] = {
          ...updatedFiles[newSelectedIndex],
          selected: true,
        };
        selectedFile = updatedFiles[newSelectedIndex];
      }

      return {
        files: updatedFiles,
        selectedFile,
      };
    });
  }

  reorderFiles = (fromIndex: number, toIndex: number) => {
    // 1. Create a brand new array copy
    const updatedFiles = [...this.state.files];
    
    // 2. Remove the video being dragged
    const [movedItem] = updatedFiles.splice(fromIndex, 1);
    
    // 3. Inject it into the new target position
    updatedFiles.splice(toIndex, 0, movedItem);
    
    // 4. Force state update with the brand new array
    this.setState({ files: updatedFiles });
  };

  addChoice = () => {
    this.setState((prevState) => {
      const updatedFiles = prevState.files.map((file) => {
        if (file.selected) {
          return {
            ...file,
            choices: [
              ...file.choices,
              {
                name: "",
                buttonType: "",
                showMenu: false,
              },
            ],
          };
        }
        return file;
      });

      const updatedSelectedFile = updatedFiles.find(file => file.selected) || prevState.selectedFile;

      return {
        files: updatedFiles,
        selectedFile: updatedSelectedFile,
      };
    });
  };
  setChoiceType = (
    type: string,
    choiceIndex: number,
    value?: string
  ) => {
    console.log('setChoiceType called:', { type, choiceIndex, value });
  
    this.setState((prevState) => {
      const updatedFiles = prevState.files.map((file) => {
        if (file.selected) {
          const updatedChoices = file.choices.map((choice, i) => {
            if (choiceIndex === i) {
              const updatedChoice: Choice = {
                ...choice,
                buttonType: type,
  
                // video: store target clip title (e.g. "Video 2") from radio pick — not the current file's id
                funnel_video_id:
                  type === "video"
                    ? value !== undefined && value !== null && String(value).length > 0
                      ? String(value)
                      : choice.funnel_video_id
                    : undefined,
  
                // link
                link:
                  type === "link"
                    ? value || choice.link
                    : choice.link,
  
                // calendar
                calendar_link:
                  type === "calendar"
                    ? value || choice.calendar_link
                    : choice.calendar_link,
              };
  
              console.log('Updated choice:', updatedChoice);
              return updatedChoice;
            }
            return choice;
          });
  
          return {
            ...file,
            choices: updatedChoices,
          };
        }
        return file;
      });
  
      const updatedSelectedFile =
        updatedFiles.find(file => file.selected) || prevState.selectedFile;
  
      return {
        files: updatedFiles,
        selectedFile: updatedSelectedFile,
      };
    });
  };

  showChoiceMenu = (choiceIndex: number) => {
    this.setState((prevState) => {
      const updatedFiles = prevState.files.map((file) => {
        if (file.selected) {
          const updatedChoices: Choice[] = file.choices.map((choice, i) => {
            if (choiceIndex === i) {
              return {
                ...choice,
                showMenu: true,
              };
            } else {
              return {
                ...choice,
                showMenu: false,
              };
            }
          });
          
          return {
            ...file,
            choices: updatedChoices,
          };
        }
        return file;
      });

      const updatedSelectedFile = updatedFiles.find(file => file.selected) || prevState.selectedFile;

      return {
        files: updatedFiles,
        selectedFile: updatedSelectedFile,
      };
    });
  };

  closeChoiceMenu = () => {
    console.log('closeChoiceMenu called');
    this.setState((prevState) => {
      const updatedFiles = prevState.files.map((file) => {
        if (file.selected) {
          const updatedChoices = file.choices.map((choice) => {
            return {
              ...choice,
              showMenu: false,
            };
          });
          
          console.log('Closing menu, new choices for file:', updatedChoices);
          return {
            ...file,
            choices: updatedChoices,
          };
        }
        return file;
      });

      const updatedSelectedFile = updatedFiles.find(file => file.selected) || prevState.selectedFile;

      return {
        files: updatedFiles,
        selectedFile: updatedSelectedFile,
      };
    });
  };

  changeChoiceName = (value: string, index: number) => {
    this.setState((prevState) => {
      const updatedFiles = prevState.files.map((file) => {
        if (file.selected) {
          const updatedChoices = file.choices.map((choice, i) => {
            if (i === index) {
              return {
                ...choice,
                name: value,
              };
            }
            return choice;
          });
          
          return {
            ...file,
            choices: updatedChoices,
          };
        }
        return file;
      });

      const updatedSelectedFile = updatedFiles.find(file => file.selected) || prevState.selectedFile;

      return {
        files: updatedFiles,
        selectedFile: updatedSelectedFile,
      };
    });
  };

  setMultipleSelection = () => {
    this.setState((prevState) => {
      return {
        allowMultipleSelection: !prevState.allowMultipleSelection,
      };
    });
  };

  setChoiceArriveTime = (value: number) => {
    this.setState({
      choicesArriveTime: value,
    });
  };

  captureThumbnail = async (): Promise<string> => {
    console.log("posst brandly reelss");
    
    try {
      console.log('Starting thumbnail capture...');
      const element = document.getElementById('newPostSettingsContainer');
      if (!element) {
        throw new Error('Element with ID "newPostSettingsContainer" not found in the DOM');
      }

      // Create video thumbnails and replace all videos with images
      const videoElements = element.querySelectorAll('video');
      const replacedVideos: { img: HTMLImageElement; originalVideo: HTMLVideoElement; parent: Node; nextSibling: Node | null }[] = [];

      videoElements.forEach((videoElement) => {
        if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
          console.log('Creating video thumbnail...');
          try {
            const canvas = document.createElement('canvas');
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              const videoThumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.95);

              // Create img element with same dimensions
              const img = document.createElement('img');
              img.src = videoThumbnailDataUrl;
              img.style.width = videoElement.offsetWidth + 'px';
              img.style.height = videoElement.offsetHeight + 'px';
              img.style.objectFit = 'cover';

              // Replace video with img
              const parent = videoElement.parentNode;
              const nextSibling = videoElement.nextSibling;
              if (parent) {
                parent.replaceChild(img, videoElement);
                replacedVideos.push({ img, originalVideo: videoElement, parent, nextSibling });
              }
            }
          } catch (error) {
            console.warn('Failed to create video thumbnail due to CORS or other error:', error);
            // Skip replacing this video, leave it as is or use placeholder
          }
        }
      });

      console.log('Element found, creating clone...');
      let dataUrl: string;
      try {
        dataUrl = await htmlToImage.toJpeg(element, {
          quality: 0.95,
          pixelRatio: 2,
          backgroundColor: '#ffffff',
          cacheBust: true, // Helps with CORS issues
          skipFonts: false, // Skip font loading which can cause CORS issues
          // Filter out video elements if they're causing issues
          filter: (node: Node) => {
            // Check if node is an Element before processing
            if (node.nodeType !== Node.ELEMENT_NODE) {
              return true; // Include text nodes, comments, etc.
            }

            const element = node as HTMLElement;

            // Skip video elements
            if (element.tagName === 'VIDEO') {
              return false;
            }

            // Skip img elements that might have CORS issues
            if (element.tagName === 'IMG') {
              const img = element as HTMLImageElement;
              // Allow data URLs and relative URLs
              if (img.src && !img.src.startsWith('data:') && !img.src.startsWith('/') && img.src.includes('http')) {
                return false;
              }
            }

            return true;
          },
        });
      } catch (imageError) {
        console.warn('htmlToImage failed, creating placeholder thumbnail:', imageError);
        // Create a placeholder thumbnail
        const canvas = document.createElement('canvas');
        canvas.width = 1100;
        canvas.height = 800;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, 1100, 800);
          ctx.fillStyle = '#000000';
          ctx.font = '48px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Thumbnail Unavailable', 550, 400);
        }
        dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      }

      // Restore video elements
      replacedVideos.forEach(({ img, originalVideo, parent }) => {
        parent.replaceChild(originalVideo, img);
      });

      // const link = document.createElement('a');
      // link.download = `everyreel-settings-${Date.now()}.jpg`;
      // link.href = dataUrl;
      // link.click();
      // console.log("🚀 ~ CreateEveryreelConfirmSettings ~ dataUrl:", dataUrl)

      this.setState({
        thumbnail: dataUrl,
      });
      await this.postFunnel();
      console.log('Thumbnail captured and downloaded successfully');
      return dataUrl;
    } catch (error) {
      console.error('Error in captureThumbnail:', {
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : error,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        screenSize: `${window.screen.width}x${window.screen.height}`
      });
      this.setState({ isLoading: false });
      throw error; // Re-throw to allow calling code to handle the error
    }
  };

  normalizeFiles = (files: File[], markFirstSelected: boolean = true) => {
    console.log("files: ", files);


    return files.map((file: File, i: number) => {
      // Calculate the base number for the title
      let titleNumber = i + 1;

      // If it's an interactive funnel, adjust the title number based on existing files
      if (this.props.postType === PostTypes.INTERACTIVE_FUNNEL && this?.state?.files) {
        titleNumber = this.state?.files?.length + i + 1;
      }

      return {
        id: file?.id,
        meta: file,
        type: file.type,
        selected: markFirstSelected && i === 0,
        title: file.name && file.name.includes('.') ? `Video ${titleNumber}` : (file.name || `Video ${titleNumber}`),
        caption: file.body || "",
        link: "",
        linkText: "Follow Us On EveryBrand",
        isLinkAllowed: true,
        choices: file?.choices || [],
      };
    });
  };

runPostFunction = async () => {
  try {
    this.setState({ isLoading: true });
    if (this.props.postType === PostTypes.SOCIAL_SCROLL) {
      await this.postSocialScroll();
      await new Promise((res) => setTimeout(res, 50000));
    } else {
      const isLiveVideo = this.props.savedFunnelFiles?.[0]?.attributes?.social_videos?.[0]?.id
      if (isLiveVideo){
        await this.postFunnel();
      } else {
        await this.captureThumbnail();
      }
    }
  } catch (e) {
    console.error("Post failed", e);
    this.setState({ isLoading: false });
  }
};

  postSocialScroll = () => {
    const formData = new FormData();

    this.state.files.forEach((file) => {
      formData.append(
        "social_scroll[social_videos_attributes][][body]",
        file?.caption
      );
      formData.append(
        "social_scroll[social_videos_attributes][][file]",
        (file.meta as unknown) as Blob
      );

      if (file.isLinkAllowed) {
        formData.append(
          "social_scroll[social_videos_attributes][][button_name]",
          file.linkText
        );
        formData.append(
          "social_scroll[social_videos_attributes][][button_url]",
          file.link
        );
      }
    });

    let requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.sendSocialScrollApiCallId = requestMessage.messageId;
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({
        token: getToken(),
      })
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.createSocialScrollEndPoint
    );
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.PostAPiMethod
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      formData
    );
    runEngine.sendMessage(requestMessage.id, requestMessage);
    return requestMessage.messageId;
  };

  dataURLToBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  postFunnel = () => {
    const isLiveVideo =
      this.props.savedFunnelFiles?.[0]?.attributes?.social_videos?.[0]?.id;
  
    const formData = new FormData();

    // ✅ Match API payload shape:
    // funnel[social_videos_attributes][i][choices_attributes][j][...]
    // (choices index is local per social video)
  
    const resolveFileIndexFromToken = (token?: string) => {
      if (!token) return -1;
      const normalizedToken = String(token);

      const idx = this.state.files.findIndex((f) => {
        const metaId = (f?.meta as any)?.id;
        return (
          String(f?.title || "") === normalizedToken ||
          (f?.id != null && String(f.id) === normalizedToken) ||
          (metaId != null && String(metaId) === normalizedToken)
        );
      });

      if (idx >= 0) return idx;

      // Fallback for older values like "Video 2"
      const m = normalizedToken.match(/^video\s+(\d+)$/i);
      if (m?.[1]) {
        const oneBased = Number(m[1]);
        if (!Number.isNaN(oneBased) && oneBased > 0) {
          const zeroBased = oneBased - 1;
          if (zeroBased >= 0 && zeroBased < this.state.files.length) return zeroBased;
        }
      }

      return -1;
    };

    const resolveSavedSocialVideoId = (fileIndex: number) => {
      const targetFile = this.state.files[fileIndex];
      if (!targetFile) return "";

      const targetSavedFunnel = this.props.savedFunnelFiles.find((funnelFile) => {
        const videoId =
          funnelFile.attributes?.social_videos?.[0]?.social_videoable_id;
        const fileId = targetFile.meta?.id ? Number(targetFile.meta.id) : null;
        const fileName = targetFile.meta?.name || "";

        if (videoId !== undefined && fileId !== null) {
          return videoId === fileId;
        }
        return funnelFile.attributes?.file_names?.includes(fileName) || false;
      });

      return targetSavedFunnel?.id ? String(targetSavedFunnel.id) : "";
    };

    this.state.files.forEach((file, index) => {
      const savedFunnel = this.props.savedFunnelFiles.find((funnelFile) => {
        const videoId =
          funnelFile.attributes?.social_videos?.[0]?.social_videoable_id;
        const fileId = file.meta?.id ? Number(file.meta.id) : null;
        const fileName = file.meta?.name || "";
  
        if (videoId !== undefined && fileId !== null) {
          return videoId === fileId;
        }
        return funnelFile.attributes?.file_names?.includes(fileName) || false;
      });
  
      // ✅ social_videos_attributes
      if (savedFunnel?.id) {
        formData.append(
          `funnel[social_videos_attributes][${index}][id]`,
          savedFunnel.id.toString()
        );
      }
  
      formData.append(
        `funnel[social_videos_attributes][${index}][name]`,
        file?.title || ""
      );
  
      formData.append(
        `funnel[social_videos_attributes][${index}][body]`,
        file?.caption || ""
      );
  
      formData.append(
        `funnel[social_videos_attributes][${index}][post_setting_attributes][choices_arrival_time]`,
        this.state.choicesArriveTime.toString()
      );
  
      file.choices?.forEach((choice: any, choiceIndex: number) => {
        const buttonType = choice?.buttonType || choice?.button_type || "";

        // Include the existing choice's id so the backend updates it in place
        // instead of creating a duplicate record.
        if (choice?.id != null) {
          formData.append(
            `funnel[social_videos_attributes][${index}][choices_attributes][${choiceIndex}][id]`,
            String(choice.id)
          );
        }

        formData.append(
          `funnel[social_videos_attributes][${index}][choices_attributes][${choiceIndex}][name]`,
          choice?.name || choice?.button_name || ""
        );

        formData.append(
          `funnel[social_videos_attributes][${index}][choices_attributes][${choiceIndex}][button_type]`,
          buttonType
        );

        if (buttonType === "video") {
          const targetFileIndex = resolveFileIndexFromToken(choice?.funnel_video_id);
          const resolvedSocialVideoId =
            targetFileIndex >= 0 ? resolveSavedSocialVideoId(targetFileIndex) : "";

          formData.append(
            `funnel[social_videos_attributes][${index}][choices_attributes][${choiceIndex}][funnel_video_id]`,
            resolvedSocialVideoId || choice?.funnel_video_id || ""
          );
        } else if (buttonType === "calendar") {
          // API expects calendar value under "link"
          formData.append(
            `funnel[social_videos_attributes][${index}][choices_attributes][${choiceIndex}][link]`,
            choice?.calendar_link || choice?.link || choice?.button_link || ""
          );
        } else {
          // "link" button and any other fallback types use "link"
          formData.append(
            `funnel[social_videos_attributes][${index}][choices_attributes][${choiceIndex}][link]`,
            choice?.link || choice?.button_link || ""
          );
        }
      });

      // Removing a choice from an already-live funnel doesn't delete it on the
      // backend just by leaving it out of the payload — it has to be explicitly
      // marked for destruction via `{ id, _destroy: true }`.
      if (isLiveVideo) {
        (file.removedChoiceIds || []).forEach((removedChoiceId, removedIndex) => {
          const destroyIndex = (file.choices?.length || 0) + removedIndex;

          formData.append(
            `funnel[social_videos_attributes][${index}][choices_attributes][${destroyIndex}][id]`,
            String(removedChoiceId)
          );
          formData.append(
            `funnel[social_videos_attributes][${index}][choices_attributes][${destroyIndex}][_destroy]`,
            "true"
          );
        });
      }
    });

    // ✅ is_live
    formData.append("funnel[is_live]", "true");
  
    // ✅ thumbnail
    if (this.state.thumbnail) {
      const thumbnailBlob = this.dataURLToBlob(this.state.thumbnail);
      formData.append("funnel[thumbnail]", thumbnailBlob, "thumbnail.jpg");
    }
  
    const isLiveVideoEndPoint = isLiveVideo
      ? `${configJSON.createFunnelEndPoint}/${this.props.savedFunnelFiles?.[0]?.id}`
      : configJSON.createFunnelEndPoint;
  
    let requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );
  
    this.sendFunnelApiCallId = requestMessage.messageId;
  
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify({
        token: getToken(),
      })
    );
  
    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      isLiveVideoEndPoint
    );
  
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      isLiveVideo ? "PUT" : configJSON.PostAPiMethod
    );
  
    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      formData
    );
  
    runEngine.sendMessage(requestMessage.id, requestMessage);
  
    return requestMessage.messageId;
  };

  async receive(_from: string, message: Message) {
    const apiRequestCallId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    if (apiRequestCallId === this.sendSocialScrollApiCallId) {
      this.handleSendSocialScrollAPIResponse(message);
    }

    if (apiRequestCallId === this.sendFunnelApiCallId) {
      this.handleSendFunnelAPIResponse(message);
    }
  }

  handleSendSocialScrollAPIResponse = (message: Message) => {
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (responseJson.data) {
      this.props.closeModal();
    }
    this.setState({ isLoading: false });
  };

  handleSendFunnelAPIResponse = (message: Message) => {
    const responseJson = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (responseJson?.data) {
      this.props.closeModal();
    }
    this.setState({ isLoading: false });
  };
}
// Customizable Area End

